import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDocumentTitle } from "@leagueplatform/web-common";
import { LoadingIndicator } from "@leagueplatform/web-common-components";
import { useIntl } from "@leagueplatform/locales";
import { Box, Button } from "@leagueplatform/genesis-core";
import { EVENT_NAME, trackAnalyticsEvent } from "@leagueplatform/analytics";
import { HEADER_NAV_BAR_DEFAULT_HEIGHT } from "@leagueplatform/solutions-foundation";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { ACCOUNT_SETUP_STEPS } from "common/constants";
import { CenteredLayout } from "components/centered-layout/centered-layout.component";
import { ConsentStep } from "components/account-setup/consent-step.component";
import { NotificationsStep } from "components/account-setup/notifications-step.component";
import { GetStartedStep } from "components/account-setup/get-started-step.component";
import { HighlightsStep } from "components/account-setup/highlights-step.component";
import { BasePage } from "components/base-page/base-page.component";
import { GenericError } from "components/generic-error/generic-error";
import { useGetUserProfile } from "hooks/queries/use-get-user-profile.hook";

type Step = (typeof ACCOUNT_SETUP_STEPS)[keyof typeof ACCOUNT_SETUP_STEPS];

const HIGHLIGHTS_CARD_MAX_WIDTH = "672px";

export const AccountSetupPage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  useDocumentTitle(formatMessage({ id: "STR_ACCOUNT_SETUP" }));
  const { data: profile, isLoading } = useGetUserProfile();
  const [step, setStep] = useState<Step>(ACCOUNT_SETUP_STEPS.CONSENT);
  const [hasError, setHasError] = useState(false);

  const handleDismissHighlights = () => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      detail: "feature highlights dismissed",
    });
    navigate(APP_MANAGED_ROUTES.HOME);
  };

  if (isLoading) return <LoadingIndicator />;

  // ftuxComplete flips to true when the Let's go CTA on the GET_STARTED step
  // calls setPostFtux. Exempt that step and HIGHLIGHTS so the user can finish
  // the masonry-driven flow without being yanked to /home.
  if (
    profile?.ftuxComplete === true &&
    step !== ACCOUNT_SETUP_STEPS.GET_STARTED &&
    step !== ACCOUNT_SETUP_STEPS.HIGHLIGHTS
  ) {
    return <Navigate to={APP_MANAGED_ROUTES.HOME} replace />;
  }

  if (hasError) {
    return <GenericError />;
  }

  // Feature Highlight is rendered with the full app shell (navbar) and a
  // card layout matching the Figma design. The other steps keep the
  // FTUX-only chrome via CenteredLayout.
  if (step === ACCOUNT_SETUP_STEPS.HIGHLIGHTS) {
    return (
      <BasePage>
        <Box
          css={{
            background: "$surfaceBackgroundSecondary",
            minHeight: `calc(100vh - ${HEADER_NAV_BAR_DEFAULT_HEIGHT}px)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            css={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              padding: "$half $one",
            }}
          >
            <Button
              priority="tertiary"
              icon="tinyClose"
              size="small"
              hideLabel
              quiet
              onClick={handleDismissHighlights}
            >
              {formatMessage({ id: "STR_CLOSE" })}
            </Button>
          </Box>
          <Box
            css={{
              width: "100%",
              maxWidth: HIGHLIGHTS_CARD_MAX_WIDTH,
            }}
          >
            <HighlightsStep />
          </Box>
        </Box>
      </BasePage>
    );
  }

  return (
    <BasePage isPrivatePage={false}>
      <CenteredLayout>
        {step === ACCOUNT_SETUP_STEPS.CONSENT && (
          <ConsentStep
            onError={() => setHasError(true)}
            onContinue={() => setStep(ACCOUNT_SETUP_STEPS.NOTIFICATIONS)}
          />
        )}
        {step === ACCOUNT_SETUP_STEPS.NOTIFICATIONS && (
          <NotificationsStep
            onContinue={() => setStep(ACCOUNT_SETUP_STEPS.GET_STARTED)}
            onSkip={() => setStep(ACCOUNT_SETUP_STEPS.GET_STARTED)}
            onError={() => setHasError(true)}
          />
        )}
        {step === ACCOUNT_SETUP_STEPS.GET_STARTED && (
          <GetStartedStep
            onContinue={() => setStep(ACCOUNT_SETUP_STEPS.HIGHLIGHTS)}
            onError={() => setHasError(true)}
          />
        )}
      </CenteredLayout>
    </BasePage>
  );
};
